import { Box, Typography, CircularProgress, Alert, Button } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import themeColors from "../theme";
import Logo from "../components/Logo";

export default function EmailChangeVerificationPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { teal, darkTeal, lightGray } = themeColors;

    const [status, setStatus] = useState("verifying"); // verifying, success, error
    const [message, setMessage] = useState("");

    useEffect(() => {
        const verifyChange = async () => {
            const token = searchParams.get("token");

            if (!token) {
                setStatus("error");
                setMessage("Invalid link. No token provided.");
                return;
            }

            try {
                const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/verify-email-change`, { token });
                setStatus("success");
                setMessage(res.data.message);
            } catch (err) {
                setStatus("error");
                setMessage(err.response?.data?.error || "Verification failed. The link may have expired.");
            }
        };

        verifyChange();
    }, [searchParams]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: lightGray,
                px: 2,
            }}
        >
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Logo />
            </Box>

            <Box
                sx={{
                    width: "100%",
                    maxWidth: 470,
                    backgroundColor: "#fff",
                    p: 5,
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center",
                }}
            >
                {status === "verifying" && (
                    <>
                        <CircularProgress sx={{ color: teal, mb: 3 }} />
                        <Typography variant="h5" fontWeight="600" sx={{ color: "#1D3557" }}>
                            Confirming your new email...
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#5A6C7D", mt: 2 }}>
                            Please wait while we update your account.
                        </Typography>
                    </>
                )}

                {status === "success" && (
                    <>
                        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: teal, mb: 2 }} />
                        <Typography variant="h5" fontWeight="600" sx={{ color: "#1D3557", mb: 2 }}>
                            Email Updated!
                        </Typography>
                        <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                            {message}
                        </Alert>
                        <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                            From now on, please sign in with your new email address.
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate("/login")}
                            sx={{
                                py: 1.5,
                                backgroundColor: teal,
                                '&:hover': { backgroundColor: darkTeal },
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.95rem"
                            }}
                        >
                            Go to Login
                        </Button>
                    </>
                )}

                {status === "error" && (
                    <>
                        <ErrorOutlineIcon sx={{ fontSize: 64, color: "#d32f2f", mb: 2 }} />
                        <Typography variant="h5" fontWeight="600" sx={{ color: "#1D3557", mb: 2 }}>
                            Verification Failed
                        </Typography>
                        <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                            {message}
                        </Alert>
                        <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                            Your link may have expired or already been used. You can request the change again from your
                            Account Settings.
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate("/account")}
                            sx={{
                                py: 1.5,
                                backgroundColor: teal,
                                '&:hover': { backgroundColor: darkTeal },
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.95rem"
                            }}
                        >
                            Go to Account Settings
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
}
